import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import Swal from "sweetalert2";
import Loader from "../utils/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Box,
    Button,
    TableSortLabel,
    Grid,
    InputAdornment,
    Switch,
} from "@mui/material";
import { fetchDeviceLog, fetchUserOption } from "../../redux/slices/logSlice";
import { useDispatch, useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { CalendarMonth, CalendarToday, Search } from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import DateRangeFilter from "../utils/DateRangeFilter";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import Apiurl from "../../Helpers/apiUrl";
import { ClickableTableLink } from "./ClickableTableLink";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../utils/FilterOptions";

const DeviceLog = () => {
    const location = useLocation();
    const navState = location.state || {};

    const dispatch = useDispatch();
    const { deviceLog, count, status } = useSelector(state => state.logs);
    const counts = count || 0;
    
    // Initialize month and year from navState if available
    const [month, setMonth] = useState(() => {
        if (navState && navState.months) return navState.months;
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        return fullFYMonths.slice(0, currentMonthIndex + 1);
    });

    const [year, setYear] = useState(() => {
        if (navState && navState.years?.length > 0) {
            const targetYrs = navState.years.map(String);
            return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
        }
        const currentYrStr = String(new Date().getFullYear());
        return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const hasValidFilters = Boolean(year?.length > 0 && month?.length > 0);

    useEffect(() => {
        if (!hasValidFilters) {
            return;
        }
        dispatch(fetchDeviceLog({
            search: searchQuery, 
            page, 
            rowsPerPage,
            month: month?.join(','),
            year: year?.map(y => y.value).join(',')
        }));
    }, [dispatch, searchQuery, page, rowsPerPage, month, year, hasValidFilters]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadExcel = () => {

        const filteredDataForExcel = deviceLog?.map((item, index) => ({
            "Sr. No": index + 1,
            "User Type": item.user_type,
            "EMP Code/GST No.": item.emp_code,
            Name: item.name,
            Role: item.role_name,
            Email: item.email,
            Phone: item.phone,
            IP: item.ip,
            Date: item ? new Date(item.created_at).toLocaleDateString("en-GB") : "",
            Time: new Date(item.updated_at).toLocaleString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        }));

        const worksheet = XLSX.utils.json_to_sheet(filteredDataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, "user_logs.xlsx");
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const nextStatus = currentStatus == 1 ? 0 : 1;

        try {
            const response = await fetch(`${Apiurl}admin/device/update-status`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("u_token")}`
                },
                body: JSON.stringify({
                    id: id,
                    device_active: nextStatus,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: "success",
                    title: "Status Updated",
                    timer: 1500,
                    showConfirmButton: false,
                });
                if (hasValidFilters) {
                    dispatch(fetchDeviceLog({ search: searchQuery, page, rowsPerPage, month: month?.join(','), year: year?.map(y => y.value).join(',') }));
                }
            } else {
                throw new Error(data.message || "Failed to update status");
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "Something went wrong!",
            });
        }
    };

    const filterConfigs = [
        { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
    ];

    const handleMonthChange = (newMonths) => {
        setMonth(newMonths);
        if (newMonths?.length > 0 && year?.length === 0) {
            const currentYrStr = String(new Date().getFullYear());
            setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
        }
        setPage(0);
    };

    const handleFilterChange = (filterId, nextSelected) => {
        if (filterId === "year") {
            setYear(nextSelected);
            if (nextSelected.length === 0) {
                setMonth([]);
            } else if (month?.length === 0) {
                const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
                const currentCalendarMonth = new Date().getMonth() + 1;
                const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
                setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
            }
        }
        setPage(0);
    };

    const handleClearFilters = () => {
        setSearchQuery("");
        setPage(0);

        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

        const currentYrStr = String(new Date().getFullYear());
        setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    };

    return (
        <MainLayout title={'Activity Log'}>

            <Box sx={{ p: 0 }}>
                <FilterBar2
                    filters={filterConfigs}
                    onFilterChange={handleFilterChange}
                    selectedMonth={month}
                    setSelectedMonth={handleMonthChange}
                    searchTerm={searchQuery}
                    setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
                    onClearFilters={handleClearFilters}
                    actions={
                        <Button
                            variant="contained"
                            startIcon={<TableChartIcon />}
                            onClick={handleDownloadExcel}
                            sx={{
                                textTransform: "none",
                                bgcolor: "#2e7d32",
                                py: "2px",
                                px: "14px",
                                borderRadius: 12,
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                                "&:hover": { bgcolor: "#1b5e20" },
                            }}
                        >
                            Excel
                        </Button>
                    }
                />

                {/* Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ background: "#04121f", color: "white" }}>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    Sr. No
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    EMP Code/GST No.
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    Name
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    Email
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    Device
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    IMEI Code
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                                    Status
                                </TableCell>

                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                if (!hasValidFilters) {
                                    return (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No data
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                                return (status === 'pending') ? (
                                    <TableBodySkeleton cols={7} rows={10} hasActions />
                                ) : deviceLog?.length > 0 ? (
                                    deviceLog.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell align="center">{item.emp_code}</TableCell>
                                            <TableCell align="center"><ClickableTableLink path="/admin/userlist" displayValue={item.user_name} searchValue={item.emp_code} date={item.user_created_at ? item.user_created_at.substring(0, 4) : null} /></TableCell>
                                            <TableCell align="center">{item.user_email}</TableCell>
                                            <TableCell align="center">{item.device_name}</TableCell>
                                            <TableCell align="center">{item.imel_code}</TableCell>
                                            <TableCell align="center">
                                                <Switch
                                                    checked={item.device_active == 1}
                                                    onChange={() => handleStatusToggle(item.id, item.device_active)}
                                                    color="primary"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No logs found
                                        </TableCell>
                                    </TableRow>
                                );
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={hasValidFilters ? counts : 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={hasValidFilters ? [10, 20, 50, 100, counts || 0] : [10, 20, 50, 100]}
                />
            </Box>
        </MainLayout>
    );
};

export default DeviceLog;